import moment from 'moment-timezone';

// All view options take a tenant `timezone` (IANA, e.g. 'Asia/Tokyo') so that
// X-axis tick labels and tooltip titles render in the location's local zone
// regardless of the browser's TZ. The data points themselves are plotted by
// their UTC instant (Chart.js TimeScale does this from Date objects), so only
// the labels shift — bucket positions stay correct.

const fallbackTz = 'UTC';

const xAxisTimeScale = (
  unit: 'month' | 'day' | 'hour',
  displayFormatKey: string,
  displayFormat: string,
  tickFormat: string,
  getLabel: () => string,
  timezone: string,
) => {
  const tz = timezone || fallbackTz;
  return {
    type: 'time' as const,
    time: {
      unit,
      // tooltipFormat is consumed by the moment adapter (browser-local by
      // default); we override the tooltip title via the plugin callback below
      // so this only matters as a fallback.
      tooltipFormat: 'll HH:mm',
      displayFormats: {
        [displayFormatKey]: displayFormat,
      },
    },
    title: {
      display: true,
      text: getLabel(),
    },
    ticks: {
      callback: function (value: number | string) {
        return moment.tz(value, tz).format(tickFormat);
      },
    },
  };
};

const tooltipInTz = (timezone: string) => {
  const tz = timezone || fallbackTz;
  return {
    callbacks: {
      title: (items: { parsed: { x: number } }[]) => {
        return moment.tz(items[0]?.parsed?.x, tz).format('ll HH:mm');
      },
    },
  };
};

const yAxis = {
  beginAtZero: true,
  min: 0,
  title: {
    display: true,
    text: 'Duration (seconds)',
  },
};

const yearViewOptions = (getLabel: () => string, timezone: string) => ({
  scales: {
    x: xAxisTimeScale('month', 'month', 'MMM', 'MMMM', getLabel, timezone),
    y: yAxis,
  },
  plugins: { tooltip: tooltipInTz(timezone) },
});

const monthViewOptions = (getLabel: () => string, timezone: string) => ({
  scales: {
    x: xAxisTimeScale('day', 'day', 'MMM D', 'DD. MM', getLabel, timezone),
    y: yAxis,
  },
  plugins: { tooltip: tooltipInTz(timezone) },
});

const weekViewOptions = (getLabel: () => string, timezone: string) => ({
  scales: {
    x: xAxisTimeScale('day', 'day', 'MMM D', 'dddd', getLabel, timezone),
    y: yAxis,
  },
  plugins: { tooltip: tooltipInTz(timezone) },
});

const dayViewOptions = (getLabel: () => string, timezone: string) => ({
  scales: {
    x: xAxisTimeScale('hour', 'hour', 'HH:mm', 'HH:mm', getLabel, timezone),
    y: yAxis,
  },
  plugins: { tooltip: tooltipInTz(timezone) },
});

export { yearViewOptions, monthViewOptions, weekViewOptions, dayViewOptions };
